/**
 * هلپرهای نرمال‌سازی مسیر رسانه.
 *
 * API پرتیفای برای فیلدهایی مثل `avatar` و `banner` همیشه رشته نمی‌فرستد؛
 * بسته به endpoint ممکن است آبجکت رسانه (`{ url }`، `{ path }`، ...)،
 * آرایه‌ای از رسانه‌ها، یا شناسه‌ی عددی بیاید. قبل از این هلپر، کد
 * مستقیم روی مقدار `.startsWith()` صدا می‌زد و با آبجکت می‌شکست
 * (`profile.avatar.startsWith is not a function`).
 */
import type { Project } from '~/types/portfolio'

/** کلیدهایی که در آبجکت رسانه ممکن است مسیر فایل را نگه دارند — به ترتیب اولویت. */
const urlKeys = [
  'url',
  'src',
  'path',
  'file',
  'image',
  'original',
  'full',
  'large',
  'medium',
  'thumbnail',
  'thumb'
] as const

/**
 * هر شکلی از مقدار رسانه را به یک رشته‌ی مسیر تبدیل می‌کند.
 * اگر مسیر قابل استخراج نبود `undefined` برمی‌گرداند تا `<img>` با
 * `src="[object Object]"` رندر نشود.
 */
export function resolveMediaUrl(value: unknown, depth = 0): string | undefined {
  if (value === null || value === undefined || depth > 3) return undefined

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
  }

  // شناسه‌ی عددی رسانه مسیر نیست؛ عمداً نادیده گرفته می‌شود.
  if (typeof value === 'number' || typeof value === 'boolean') return undefined

  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = resolveMediaUrl(item, depth + 1)
      if (resolved) return resolved
    }
    return undefined
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const key of urlKeys) {
      const resolved = resolveMediaUrl(record[key], depth + 1)
      if (resolved) return resolved
    }
  }

  return undefined
}

/**
 * فیلدهای رسانه‌ای یک پروژه را به رشته‌ی مسیر تبدیل می‌کند.
 *
 * مقدار خام `banner` از API همیشه رشته نیست. اگر بدون نرمال‌سازی به
 * `<img :src>` برسد، Vue آن را با `String()` رندر می‌کند و مرورگر یک آدرس
 * بی‌معنی (`[object Object]` یا شناسه‌ی عددی) روی همین دامنه درخواست می‌کند.
 * چون فایل استاتیکی با آن نام وجود ندارد، درخواست به رندرکننده‌ی Nuxt می‌رسد
 * و به‌جای تصویر یک صفحه‌ی HTML برمی‌گردد — برای هر کارت پروژه یکی.
 */
export function normalizeProjectMedia(project: Project): Project {
  const gallery = Array.isArray(project.gallery)
    ? project.gallery
      .map(item => resolveMediaUrl(item))
      .filter((item): item is string => Boolean(item))
    : []

  return {
    ...project,
    banner: resolveMediaUrl(project.banner) ?? '',
    gallery
  }
}

/**
 * مسیر رسانه را به URL مطلق تبدیل می‌کند (برای og:image و JSON-LD که
 * مسیر نسبی را قبول نمی‌کنند).
 */
export function resolveAbsoluteMediaUrl(
  value: unknown,
  origin: string
): string | undefined {
  const url = resolveMediaUrl(value)
  if (!url) return undefined
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:')) return url

  return `${origin.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
}
